import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { XIcon, UserIcon, MailIcon, LockIcon, EyeIcon, EyeOffIcon, LoaderIcon } from 'lucide-react';
import { Logo } from './Logo';
import { useTheme } from '../context/ThemeContext';
interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}
type AuthMode = 'login' | 'register';
export function AuthModal({
  isOpen,
  onClose
}: AuthModalProps) {
  const {
    isArabic
  } = useLanguage();
  const {
    theme
  } = useTheme();
  const {
    login,
    register,
    loading
  } = useAuth();
  const [mode, setMode] = useState<AuthMode>('login');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const toggleMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setErrors({});
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (mode === 'register' && !formData.name.trim()) {
      newErrors.name = isArabic ? 'الاسم مطلوب' : 'Name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = isArabic ? 'البريد الإلكتروني مطلوب' : 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = isArabic ? 'البريد الإلكتروني غير صالح' : 'Email is invalid';
    }
    if (!formData.password) {
      newErrors.password = isArabic ? 'كلمة المرور مطلوبة' : 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = isArabic ? 'يجب أن تتكون كلمة المرور من 6 أحرف على الأقل' : 'Password must be at least 6 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (mode === 'login') {
      const success = await login(formData.email, formData.password);
      if (success) {
        onClose();
      }
    } else {
      const success = await register(formData.name, formData.email, formData.password);
      if (success) {
        onClose();
      }
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-id="element-161">
      <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-162">
        <div className="absolute top-0 right-0 p-2" data-id="element-163">
          <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" aria-label="Close" data-id="element-164">
            <XIcon className="w-5 h-5" data-id="element-165" />
          </button>
        </div>
        <div className="p-8" data-id="element-166">
          <div className="flex items-center justify-center mb-6" data-id="element-167">
            <Logo width={40} height={40} color={theme === 'dark' ? '#60A5FA' : '#3B82F6'} data-id="element-168" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white ml-3" data-id="element-169">
              {mode === 'login' ? isArabic ? 'تسجيل الدخول' : 'Sign In' : isArabic ? 'إنشاء حساب' : 'Create Account'}
            </h2>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-170">
            {mode === 'register' && <div data-id="element-171">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-172">
                  {isArabic ? 'الاسم' : 'Name'}
                </label>
                <div className="relative" data-id="element-173">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-174">
                    <UserIcon className="h-5 w-5 text-gray-400" data-id="element-175" />
                  </div>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder={isArabic ? 'أدخل اسمك' : 'Enter your name'} className={`w-full pl-10 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-176" />
                </div>
                {errors.name && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-177">
                    {errors.name}
                  </p>}
              </div>}
            <div data-id="element-178">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-179">
                {isArabic ? 'البريد الإلكتروني' : 'Email'}
              </label>
              <div className="relative" data-id="element-180">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-181">
                  <MailIcon className="h-5 w-5 text-gray-400" data-id="element-182" />
                </div>
                <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder={isArabic ? 'أدخل بريدك الإلكتروني' : 'Enter your email'} className={`w-full pl-10 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-183" />
              </div>
              {errors.email && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-184">
                  {errors.email}
                </p>}
            </div>
            <div data-id="element-185">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-186">
                {isArabic ? 'كلمة المرور' : 'Password'}
              </label>
              <div className="relative" data-id="element-187">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-188">
                  <LockIcon className="h-5 w-5 text-gray-400" data-id="element-189" />
                </div>
                <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange} placeholder={isArabic ? 'أدخل كلمة المرور' : 'Enter your password'} className={`w-full pl-10 pr-10 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.password ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-190" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-191">
                  {showPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-192" /> : <EyeIcon className="h-5 w-5" data-id="element-193" />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-194">
                  {errors.password}
                </p>}
            </div>
            <button type="submit" disabled={loading} className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center" data-id="element-195">
              {loading ? <LoaderIcon className="w-5 h-5 animate-spin" data-id="element-196" /> : mode === 'login' ? isArabic ? 'تسجيل الدخول' : 'Sign In' : isArabic ? 'إنشاء حساب' : 'Create Account'}
            </button>
          </form>
          <div className="mt-6 text-center" data-id="element-197">
            <button onClick={toggleMode} className="text-blue-600 dark:text-blue-400 hover:underline text-sm" data-id="element-198">
              {mode === 'login' ? isArabic ? 'ليس لديك حساب؟ إنشاء حساب' : "Don't have an account? Sign up" : isArabic ? 'لديك حساب بالفعل؟ تسجيل الدخول' : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    </div>;
}